// @ts-check

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import {
  CustomizerEngine,
  maskScan,
  metalTintedUrl,
  silhouetteScan,
} from '@theme/custom.customizer-engine';
import {
  apertureFromMask,
  laserCalloutLeading,
  LASER_CALLOUT_INK,
  planLaserCallouts,
} from '@theme/custom.customizer-callouts';
import { CustomizerAdapter, planFrame } from '@theme/custom.customizer-adapter';
import {
  fingerprintFace,
  fingerprintScan,
  fingerprintScanKind,
  fingerprintPreviewSpec,
} from '@theme/custom.customizer-fingerprint';
import {
  faceAreaSteps,
  isFaceModeControl,
  isScanUpload,
  photoFinish,
  photoMethodControl,
  photoModeValue,
} from '@theme/custom.customizer-store';
import { readIsland } from '@theme/custom.customizer-resolve';
import { holdDialogCancel } from '@theme/custom.customizer-layer';

/** Takes the selling points off the screen and leaves them in the accessibility tree. */
const LASER_POINTS_ASSISTIVE = 'custom-customizer-preview__points--assistive';

/** How long a scan image that failed to load is left alone before a draw asks for it again. */
const SCAN_RETRY_MS = 30_000;

/** @typedef {import('@theme/events').CustomizerUpdateEvent} CustomizerUpdateEvent */
/** @typedef {import('@theme/custom.customizer-store').CustomizerStore} CustomizerStore */
/** @typedef {import('@theme/custom.customizer-store').CustomizerStep} CustomizerStep */
/**
 * One resolved frame: what to paint, and everything that is decided by which source it came from.
 * `kind` is a single value, so the source cannot contradict itself or the url it describes.
 * @typedef {object} Frame
 * @property {string | null} url - What the stage shows; null when nothing resolved at all.
 * @property {'photo' | 'composite' | 'raw'} kind - Which source it came from.
 * @property {string | null} inset - What rides in the Review corner, null when nothing does.
 */

/**
 * @typedef {object} Refs
 * @property {HTMLButtonElement} toggle
 * @property {HTMLImageElement} [ghost] - The crossfade ghost: holds the OUTGOING composite frame
 *   over the incoming one and fades out once the new frame is paint-ready (#render / #fadeGhost).
 * @property {HTMLImageElement} [laserPromo] - The laser promo overlay, and the PRIMARY treatment:
 *   the snippet renders it only when the section resolved art, from the product or from its shape
 *   family. Carries `data-src-left` (always), `data-src-right` (only where right-inside art was
 *   resolved) and `data-promo-own` (only where the art came from the product); the component swaps
 *   `src` between the two per face.
 * @property {SVGSVGElement} [laserCallouts] - The same three selling points DRAWN over this face's
 *   own aperture, for every product the graphic cannot serve. Carries the translated copy on
 *   `data-label-*`; the component scans the face's mask and writes the lines, dots and labels.
 * @property {HTMLElement} [laserPoints] - The last resort: the three points as plain badges, for a
 *   face with neither promo art nor a readable aperture (#syncLaserPromo picks between the three).
 * @property {HTMLElement} [fullscreen] - The fullscreen viewer, hidden until a Preview action.
 * @property {HTMLImageElement} [fullscreenImage] - Its <img>, filled from the live composite.
 * @property {HTMLButtonElement} [fullscreenClose] - Its close (X) button, focused on open.
 * @property {HTMLButtonElement} [sizeToggle] - The Size step's ruler toggle (per-size image ⇄
 *   at-scale family shot) — replaces the zoom toggle there; the snippet renders it only when any
 *   size image exists.
 * @property {HTMLImageElement} [sizeComparison] - The Size-step art shown while [comparing] (the
 *   whole Size step). `data-variant-charts` holds the per-size chart map, `data-default-src` the
 *   product-level per-size fallback, and `data-ruler-src` the at-scale family shot (the
 *   customizer_shape metaobject image) the ruler toggle swaps to (#syncSizeComparison).
 * @property {HTMLImageElement} [fingerprintBubble] - The uploaded scan in a small round bubble
 *   over the stage, shown on the upload step and on Review, each behind its own store setting.
 * @property {HTMLButtonElement} [reviewInsetButton] - The Review step's proof inset: the rendered
 *   design (composite, last-resort plate + engraving, or the raw upload) as a small square over
 *   the variant photo, so Review leads with the product and still proves what was personalized.
 *   A button: pressing it swaps the two frames.
 * @property {HTMLImageElement} [reviewInset] - The <img> inside that button.
 * @property {HTMLElement} [rawNotice] - The bottom-centre notice shown when the preview is the
 *   shopper's raw upload rather than a composite: this product has no art and no aperture,
 *   so what they see is not what the piece will look like. Hidden on every other render.
 *
 * @extends Component<Refs>
 */
export class CustomizerPreview extends Component {
  requiredRefs = ['toggle'];

  /** @type {CustomizerEngine} */
  #engine = new CustomizerEngine();

  /** @type {CustomizerAdapter | null} */
  #adapter = null;

  /** @type {number} */
  #raf = 0;

  /** @type {number} */
  #renderToken = 0;

  /** Invalidates work tied to replaced markup or a previous connection. */
  #surfaceVersion = 0;

  /** @type {CustomizerStore | null} */
  #boundStore = null;

  /** @type {string | null} - URL last written to the composite <img>. Identical output means no
   *   visible change, so the swap (and the loader that covers it) is skipped entirely. */
  #lastUrl = null;

  /** @type {number} - Renders in flight; the `loading` attribute is on while > 0. */
  #pending = 0;

  /** @type {number} */
  #loadingTimer = 0;

  /** @type {boolean} - Whether the one-time idle cache warmup has been scheduled. */
  #warmedUp = false;

  /** @type {HTMLElement | null} - Focus to restore when the fullscreen viewer closes (the Preview
   *   action that opened it), so keyboard focus doesn't strand on the now-hidden close button. */
  #fullscreenReturnFocus = null;

  /** Stops the overlay's back gesture closing only the fullscreen viewer. */
  #releaseFullscreenCancel = () => {};

  /** @type {boolean} - Whether the previous #syncSizeMode saw the Size step. Used to reset to the
   *   per-size image only when ENTERING the step, not on every in-step store update. */
  #wasOnSize = false;

  /** @type {boolean} - Ruler toggle state on the Size step: false = the SELECTED size's own image
   *   (landing view, tracks size picks), true = the at-scale family shot (`data-ruler-src`). */
  #atScale = false;

  /** @type {boolean} - Review inset swapped: the fallback holds the stage, the product photo the
   *   thumbnail. Reset when the inset leaves the screen, so Review always opens product-first. */
  #insetSwapped = false;

  /** @type {Record<string, string> | null} - Parsed `{ "<variant.id>": "<url>" }` per-size chart
   *   map from the size-comparison <img>'s `data-variant-charts`. Parsed once, then reused. */
  #sizeChartMap = null;

  /** @type {boolean} - A {@link handBack} render is composing the frame the photo editor is about
   *   to reveal, so the `editing` gate below must let this one through. */
  #handingBack = false;

  /** @type {CustomizerStore | null} - A store update the `editing` gate swallowed during a
   *   hand-back, replayed once that render lands so the hero cannot keep a superseded frame. */
  #pendingStore = null;

  /** @type {string} - Width/height the frame is pinned to while step art owns the stage, '' for
   *   the square composite stage. Compared before writing, so an unchanged frame costs no style. */
  #artRatio = '';

  /** @type {number} - Guards the callout drawing: a mask decodes asynchronously, so a face the
   *   shopper has already left must not paint over the one they are looking at. */
  #calloutToken = 0;

  /** @type {Map<string, Promise<HTMLImageElement | null>>} - Masks and base art decoded for the
   *   callout scan. */
  #scanImages = new Map();

  /** @type {HTMLCanvasElement | null} - Kept for measuring callout type; making one per line is a
   *   layout cost on a step that redraws with every control. */
  #textCanvas = null;

  /**
   * The adapter and engine the hero renders through. Lent to the add-to-cart proof pass so a face
   * the shopper already looked at is a cache hit instead of a second fetch and composite.
   * @returns {{ adapter: CustomizerAdapter | null, engine: CustomizerEngine }}
   */
  get renderers() {
    return { adapter: this.#adapter, engine: this.#engine };
  }

  connectedCallback() {
    super.connectedCallback();
    this.#resetSurface();
    this.#adapter = new CustomizerAdapter({ assets: this.#readAssets() });
    document.addEventListener(ThemeEvents.customizerUpdate, this.#handleStoreUpdate);
    document.addEventListener(ThemeEvents.customizerPhotoEdit, this.#handlePhotoEdit);
    document.addEventListener(ThemeEvents.customizerOpen, this.#handleOverlayOpen);
    // `load` does not bubble, so it is caught in the capture phase: every art swap lands here,
    // whichever path pointed the <img> at it (a size pick, the ruler, a chain drill).
    this.addEventListener('load', this.#handleArtLoad, true);
    // The host may have broadcast before this component subscribed (upgrade order), so seed from
    // its store directly: a wizard that OPENS on the Size step must show the ruler right away.
    const host = /** @type {{ store?: CustomizerStore } | null} */ (
      this.closest('custom-customizer')
    );
    if (host?.store) {
      this.#boundStore = host.store;
      this.#syncSizeMode(host.store);
      this.#syncSizeComparison(host.store);
      this.#syncStageRatio(host.store);
      this.#syncFingerprintBubble(host.store);
    }
  }

  updatedCallback() {
    super.updatedCallback();
    this.#resetSurface();
    this.#adapter = new CustomizerAdapter({ assets: this.#readAssets() });
    const host = /** @type {{ store?: CustomizerStore } | null} */ (
      this.closest('custom-customizer')
    );
    this.#boundStore = host?.store ?? null;
    if (this.#boundStore) this.#scheduleRender(this.#boundStore);
  }

  /** Cache entries describe render specs; frame bookkeeping belongs to the live DOM and store. */
  #resetSurface() {
    this.#surfaceVersion += 1;
    this.#renderToken += 1;
    this.#calloutToken += 1;
    cancelAnimationFrame(this.#raf);
    clearTimeout(this.#loadingTimer);
    this.#pending = 0;
    this.toggleAttribute('loading', false);
    this.#handingBack = false;
    this.#pendingStore = null;
    this.#lastUrl = null;
    this.#boundStore = null;
    this.#warmedUp = false;
    this.#sizeChartMap = null;
    this.#wasOnSize = false;
    this.#insetSwapped = false;
    this.#setAtScale(false);
    this.#setComparing(false);
    this.#setArtRatio('');
    this.refs.ghost?.classList.remove('custom-customizer-preview__ghost--showing');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resetSurface();
    this.#adapter = null;
    document.removeEventListener(ThemeEvents.customizerUpdate, this.#handleStoreUpdate);
    document.removeEventListener(ThemeEvents.customizerPhotoEdit, this.#handlePhotoEdit);
    document.removeEventListener(ThemeEvents.customizerOpen, this.#handleOverlayOpen);
    this.removeEventListener('load', this.#handleArtLoad, true);
    document.removeEventListener('keydown', this.#onFullscreenKeydown, true);
    this.#releaseFullscreenCancel();
  }

  /** @param {Event} event */
  #handleStoreUpdate = (event) => {
    const { configId, store } = /** @type {CustomizerUpdateEvent} */ (event).detail;
    const host = /** @type {(HTMLElement & { store?: CustomizerStore }) | null} */ (
      this.closest('custom-customizer')
    );
    if (!host || configId !== host.dataset.configId || store !== host.store) return;
    if (store !== this.#boundStore) {
      this.updatedCallback();
      return;
    }
    this.#scheduleRender(/** @type {CustomizerStore} */ (store));
  };

  /**
   * The photo editor taking over or handing back. While it is active the composite `<img>` is
   * hidden (`editing`), so a re-render per drag would only add a wasted PNG encode and a stutter.
   * Skip those; refresh once when it hands back.
   * @param {Event} event
   */
  #handlePhotoEdit = (event) => {
    const { configId, active } = /** @type {CustomEvent} */ (event).detail;
    const host = /** @type {{ dataset: DOMStringMap, store?: CustomizerStore } | null} */ (
      this.closest('custom-customizer')
    );
    if (!host || configId !== host.dataset.configId || active || !host.store) return;
    const store = host.store;
    // Canvas editors use `handBack()` to compose before reveal; this covers the remaining cases.
    // Defer because listeners share this event and `handBack()` runs synchronously.
    void Promise.resolve().then(() => {
      if (this.#handingBack) return;
      this.#scheduleRender(store);
    });
  };

  /**
   * Coalesce a render to the next frame, skipping it while the photo editor owns the preview: the
   * composite is hidden then, so rendering it only adds drag stutter.
   * The check runs inside the frame callback so a hand-back that lands in the same event as the
   * store update (a step change deactivates the editor synchronously) still renders.
   * @param {CustomizerStore} store
   */
  #scheduleRender(store) {
    if (store !== this.#boundStore) return;
    cancelAnimationFrame(this.#raf);
    this.#raf = requestAnimationFrame(() => {
      // Ahead of the editor gate: Select Style commits the method while the editor owns the frame,
      // and the laser claims depend on it. Only attribute toggles; CSS hides them until hand-back.
      this.#syncLaserPromo(store);
      if (this.hasAttribute('editing')) {
        // A hand-back is already composing the frame the editor will reveal; remember the update
        // rather than dropping it behind the gate, and let that render replay it.
        if (this.#handingBack) this.#pendingStore = store;
        return;
      }
      void this.#render(store);
    });
  }

  /**
   * The photo editor HANDING THE PREVIEW BACK: recomposite while its canvas is still painted over
   * the frame, and resolve once the new composite is in the `<img>`. The editor keeps `[editing]`
   * until then, so revealing the hero cannot flash the previous frame and make Confirm & Continue
   * look like several things happening at once.
   *
   * Public because the editor calls it directly, the same way {@link toggle} calls the editor's
   * `refresh()` in the other direction: this is one hand-off between two halves of one frame, not a
   * state change anything else subscribes to.
   * @param {CustomizerStore} store
   * @returns {Promise<void>}
   */
  async handBack(store) {
    if (store !== this.#boundStore) return;
    const version = this.#surfaceVersion;
    cancelAnimationFrame(this.#raf);
    this.#handingBack = true;
    try {
      await this.#render(store);
    } finally {
      if (version === this.#surfaceVersion) {
        this.#handingBack = false;
        // Replay updates swallowed in flight; unchanged URLs stop before repainting the frame.
        const pending = this.#pendingStore;
        this.#pendingStore = null;
        if (pending) void this.#render(pending);
      }
    }
  }

  /**
   * @param {CustomizerStore} store
   * @returns {Promise<void>}
   */
  async #render(store) {
    const version = this.#surfaceVersion;
    this.#syncFingerprintBubble(store);
    this.#syncLaserPromo(store);
    this.#syncSizeMode(store);
    this.#syncSizeComparison(store);
    this.#syncStageRatio(store);
    const image = this.querySelector('.custom-customizer-preview__image');
    const adapter = this.#adapter;
    if (!(image instanceof HTMLImageElement) || !adapter) return;

    // Some steps show a direct image instead of the composite (CustomizerStore#heroImageOverride).
    // Bump the token first so an in-flight composite render cannot overwrite it a beat later.
    const override = store.heroImageOverride;
    if (override) {
      const token = ++this.#renderToken;
      // KEY-DECISION 2026-08-07: every frame gets the tone-shown vs tone-picked check, so even a
      // direct photo is offered the picked metal; metalTintedUrl returns it unchanged if it can't.
      const tint = await adapter.directTint(store);
      const url = (await metalTintedUrl(override, tint)) ?? override;
      if (token !== this.#renderToken) return;
      // This frame is a direct photo, never a raw upload, so a notice left by an earlier raw-photo
      // render must not caption it (raw photo on the face step, this override one step later).
      this.refs.rawNotice?.toggleAttribute('hidden', true);
      this.#syncReviewInset(null);
      // No ghost on this one: nothing composed it, and fading a composite still on screen into a
      // catalogue photo reads as the wrong picture arriving.
      await this.#paint(image, { url, ghost: false }, token);
      return;
    }

    const token = ++this.#renderToken;
    this.#beginLoading();
    try {
      const frame = await this.#composeFrame(store, adapter);
      if (token !== this.#renderToken) return;
      // After the token check, so a superseded pass leaves no notice or inset on a frame it no
      // longer owns (flip the metal mid-render and the newer pass decides).
      this.refs.rawNotice?.toggleAttribute('hidden', frame.kind !== 'raw');
      this.#syncReviewInset(frame.inset);
      const { url } = frame;
      if (!url) {
        this.#fadeGhost();
        return;
      }
      await this.#paint(image, { url, composite: frame.kind === 'composite' }, token);
    } finally {
      if (version === this.#surfaceVersion) {
        this.#endLoading();
        this.#warmup(store);
      }
    }
  }

  /**
   * THE FRAME THIS STORE STATE ASKS FOR. {@link planFrame} decides which sources are shown; this
   * resolves only those, so a render the plan never shows is never composited and a photo it never
   * shows is never tone-checked. A failure anywhere falls back to the product photo.
   * @param {CustomizerStore} store
   * @param {CustomizerAdapter} adapter
   * @returns {Promise<Frame>}
   */
  async #composeFrame(store, adapter) {
    const photo = store.selectedVariant?.image ?? null;
    /** @type {Awaited<ReturnType<CustomizerAdapter['buildSpec']>>} */
    let spec = null;
    /** @type {string | null} */
    let rendered = null;
    /** @type {import('@theme/custom.customizer-adapter').FramePlan} */
    let plan = { stage: 'photo', inset: null };
    try {
      spec = fingerprintPreviewSpec(await adapter.buildSpec(store), store);
      plan = planFrame(spec, store, {
        hasPhoto: Boolean(photo),
        scanFace: fingerprintFace(store),
        swapped: this.#insetSwapped,
      });
      if (spec && (plan.stage === 'render' || plan.inset === 'render')) {
        // Last-resort raw upload: no art and no aperture resolved, so there is nothing to
        // composite onto; the adapter hands the upload back and the preview shows it flat.
        rendered =
          'rawPhoto' in spec ? spec.rawPhoto : await this.#engine.render(/** @type {any} */ (spec));
      }
    } catch (error) {
      console.error('[customizer] preview render failed', error);
      spec = null;
      rendered = null;
      plan = { stage: 'photo', inset: null };
    }

    // The photo is also the floor: if the render fails, the product photo still shows.
    // KEY-DECISION 2026-08-07: every frame gets the tone-shown vs tone-picked check, so the shot is
    // resolved through it once here, for the raw-scan stage and this floor too.
    const wantsPhoto = plan.stage === 'photo' || plan.inset === 'photo' || rendered === null;
    const shot =
      wantsPhoto && photo
        ? ((await metalTintedUrl(photo, await adapter.directTint(store))) ?? photo)
        : null;
    const url = (plan.stage === 'render' ? (rendered ?? shot) : (shot ?? rendered)) ?? null;
    const fromRender = url !== null && url === rendered;
    return {
      url,
      kind: !fromRender ? 'photo' : spec && 'rawPhoto' in spec ? 'raw' : 'composite',
      inset: plan.inset === 'render' ? rendered : plan.inset === 'photo' ? shot : null,
    };
  }

  /**
   * PUT ONE FRAME ON THE STAGE - the only place that writes the hero `<img>`, so the direct-photo
   * and composite paths share one copy of the bookkeeping (`#lastUrl` and the crossfade).
   * Unchanged output means no swap and no loader, just
   * release a ghost a superseded pass may have left showing.
   * @param {HTMLImageElement} image
   * @param {{ url: string, ghost?: boolean, composite?: boolean }} frame - `ghost: false`
   *   hard-cuts. `composite: true` marks a frame whose blob the ENGINE owns, and may therefore
   *   have released since the render that produced it.
   * @param {number} token
   * @returns {Promise<void>}
   */
  async #paint(image, frame, token) {
    if (frame.url === this.#lastUrl) {
      this.#fadeGhost();
      return;
    }
    // The blob may have been revoked since its render (cache eviction or engine `forget`): keep the
    // current frame. Checked before `#lastUrl` moves, or an identical later render never paints.
    if (frame.composite && !this.#engine.isCached(frame.url)) {
      this.#fadeGhost();
      return;
    }
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    const previous = this.#lastUrl;
    // Track what is on screen before the decode, or the next render matches a stale `#lastUrl` and
    // short-circuits, leaving the chain image stuck when the shopper backs off the chain step.
    this.#lastUrl = frame.url;
    // The ghost holds the OUTGOING frame over the live one while the new one decodes, so cached
    // swaps morph instead of hard-cutting. Both fade at once; sequential read as a double cut.
    const { ghost } = this.refs;
    // An engine composite blob may have been evicted AND revoked since it painted, and a revoked
    // src paints a broken ghost, worse than the hard cut. Non-blob URLs are always safe.
    const ghostSafe =
      previous && (!previous.startsWith('blob:') || this.#engine.isCached(previous));
    if (frame.ghost !== false && ghost && previous && ghostSafe) {
      ghost.src = previous;
      ghost.classList.add('custom-customizer-preview__ghost--showing');
    }
    image.src = frame.url;
    // The swap is where the preview visibly lags (a decode, or a whole CDN fetch for a variant
    // photo), so hold the loader until decode() settles: it resolves paint-ready, rejects if not.
    await image.decode().catch(() => {});
    if (token === this.#renderToken) this.#fadeGhost();
  }

  /** Releases the ghost next frame, after the browser commits the new composite beneath it, so
   * the transition morphs one into the other; the timing also serializes racing passes. */
  #fadeGhost() {
    const { ghost } = this.refs;
    if (!ghost) return;
    const token = this.#renderToken;
    requestAnimationFrame(() => {
      if (token === this.#renderToken)
        ghost.classList.remove('custom-customizer-preview__ghost--showing');
    });
  }

  /**
   * One-time cache warmup: hand the engine every asset the schema could ask for soon (each face's
   * base art for every metal, masks, covers, stone art, the engraving fonts) so later step, metal
   * and stone switches render warm instead of paying a cold fetch and decode under the loader.
   * Scheduled after the first render settles, in idle time, so it never competes with the composite
   * the shopper is actually looking at.
   * @param {CustomizerStore} store
   */
  #warmup(store) {
    if (this.#warmedUp || !this.#adapter) return;
    this.#warmedUp = true;
    const adapter = this.#adapter;
    // Safari still ships no requestIdleCallback — a short timeout is close enough there.
    const schedule =
      window.requestIdleCallback?.bind(window) ??
      ((/** @type {() => void} */ callback) => window.setTimeout(callback, 300));
    schedule(() => {
      if (adapter !== this.#adapter || store !== this.#boundStore) return;
      const { urls, fonts } = adapter.warmupAssets(store);
      this.#engine.preload(urls, fonts);
    });
  }

  /**
   * The laser selling points, shown while the CURRENT step's photo flow sells a laser engraving.
   * Three treatments, and the artwork still leads. Artwork picked on the PRODUCT
   * (`data-promo-own`) was drawn against this product's window, so it shows over whatever art the
   * face draws; the shape family's pair is only valid over that family's own template art.
   *
   * Everywhere else the callouts are DRAWN, over this face's own aperture (#drawLaserCallouts), so
   * a product with its own art or a family with no artwork gets the same design rather than a row
   * of badges. The badges remain the last resort, for a face whose aperture cannot be read at all.
   * @param {CustomizerStore} store
   */
  #syncLaserPromo(store) {
    const { laserPromo, laserCallouts } = this.refs;
    const step = this.#laserSellingStep(store);
    const active = Boolean(step) && this.#laserPromoActive(store, step);
    const own = laserPromo?.dataset.promoOwn === 'true';
    const shaped =
      active && Boolean(laserPromo) && (own || this.#faceDrawsTemplateArt(store, step));
    laserPromo?.toggleAttribute('hidden', !shaped);
    if (shaped) {
      laserCallouts?.toggleAttribute('hidden', true);
      this.#showLaserPoints('off');
      // The promo has baked-in callout text, so the right inside face is not a CSS mirror: it uses
      // a separately drawn file, emitted only when the store has one. Other faces keep the left.
      const { srcLeft, srcRight } = laserPromo?.dataset ?? {};
      const next = step?.side === 'inside_right' && srcRight ? srcRight : srcLeft;
      if (next && laserPromo?.getAttribute('src') !== next) laserPromo?.setAttribute('src', next);
      return;
    }
    if (!active) {
      this.#calloutToken += 1;
      laserCallouts?.toggleAttribute('hidden', true);
      this.#showLaserPoints('off');
      return;
    }
    this.#drawLaserCallouts(store);
  }

  /**
   * THE THREE CLAIMS IN WORDS, which is the only form a screen reader can use. The drawing is
   * decorative by construction - dots and leader lines carry the meaning visually, and read aloud
   * in document order they would say nothing - so the SVG stays `aria-hidden` and this list holds
   * the copy for all three treatments. `assistive` takes it off the screen and leaves it in the
   * accessibility tree, so the drawn callouts announce exactly what the badges do.
   * @param {'badges' | 'assistive' | 'off'} mode
   */
  #showLaserPoints(mode) {
    const { laserPoints } = this.refs;
    if (!laserPoints) return;
    laserPoints.toggleAttribute('hidden', mode === 'off');
    laserPoints.classList.toggle(LASER_POINTS_ASSISTIVE, mode === 'assistive');
  }

  /**
   * THE FACE THE POINTS SPEAK FOR. Every step but one sells its own: the claims are about the
   * engraving this screen is taking a photo for.
   *
   * The Review screen is the exception. It takes no photo of its own, but it is where the shopper
   * looks at the finished piece and decides, so the points ride the face its tabs are showing -
   * whichever step took that face's photo. A merchant setting, because keeping a marketing claim on
   * the confirmation screen is a marketing call, not a layout one.
   * @param {CustomizerStore} store
   * @returns {CustomizerStep | null} Null where nothing on screen sells an engraving.
   */
  #laserSellingStep(store) {
    if (!store.isReviewStep) return store.currentStepConfig;
    if (!this.hasAttribute('laser-points-on-review')) return null;
    const side = store.previewSide;
    const steps = store.configSchema?.steps ?? [];
    return (
      steps.find(
        (step) =>
          (!side || step.side === side) &&
          (step.controls ?? []).some((control) => control.type === 'upload')
      ) ?? null
    );
  }

  /**
   * THE DRAWN CALLOUTS: three dots inside the window the shopper's photo lands in, each on a
   * leader line to a label above or below the piece - the heart family's artwork, rebuilt from
   * this face's own aperture so it frames THIS window rather than a heart's.
   *
   * The aperture is the face's MASK - the shape template's, the art set's or the per-product
   * override's, whichever the art ladder resolved, asked through the adapter so this cannot
   * disagree with what the hero composited. Nothing here reads the art SOURCE: a dot scanned off
   * the mask sits inside the window whoever authored it, which is exactly why a product carrying
   * its own art can have the design too. A face with no mask has no window and gets the badges.
   * @param {CustomizerStore} store
   */
  #drawLaserCallouts(store) {
    const { laserCallouts } = this.refs;
    if (!laserCallouts) {
      this.#showLaserPoints('badges');
      return;
    }
    const surface = this.#photoSurface(store);
    const token = (this.#calloutToken += 1);
    const show = (
      /** @type {import('@theme/custom.customizer-callouts').Aperture | null} */ a,
      /** @type {import('@theme/custom.customizer-callouts').Aperture | null} */ piece = null
    ) => {
      if (token !== this.#calloutToken) return;
      const drawn = a ? this.#paintLaserCallouts(laserCallouts, a, piece) : false;
      laserCallouts.toggleAttribute('hidden', !drawn);
      this.#showLaserPoints(drawn ? 'assistive' : 'badges');
    };
    // No window here: the photo composites as a plain rectangle, which is what the badges are for.
    // A declared ZONE is not a window: nothing on screen is cut to it, so a leader line would miss.
    if (!surface) {
      show(null);
      return;
    }
    // Scanning needs decoded pixels, so the first visit to a face paints the badges for one frame
    // and swaps to the drawing when the mask lands; every later visit is served from the caches.
    void Promise.all([this.#loadScan(surface.mask), this.#scanPiece(surface.base)]).then(
      ([element, piece]) => {
        const scan = element ? maskScan(surface.mask, element) : null;
        show(scan ? apertureFromMask(scan) : null, piece);
      }
    );
  }

  /**
   * THE PIECE, not its window: the base art's own outline, so a label goes beside the metal rather
   * than across it. Most bases are opaque JPEGs shot on white, so the alpha read falls through to
   * the luminance one. Null for a full-bleed composite; the label stays where the plan puts it.
   * @param {string | null} url
   * @returns {Promise<import('@theme/custom.customizer-callouts').Aperture | null>}
   */
  async #scanPiece(url) {
    if (!url) return null;
    const element = await this.#loadScan(url);
    const scan = element ? (maskScan(url, element) ?? silhouetteScan(url, element)) : null;
    return scan ? apertureFromMask(scan) : null;
  }

  /**
   * Lay the plan out into the overlay. Percentages throughout: the SVG's viewBox is the frame, and
   * `preserveAspectRatio` fits it the way `object-fit: contain` fits the composite under it.
   * @param {SVGSVGElement} svg
   * @param {import('@theme/custom.customizer-callouts').Aperture} aperture
   * @param {import('@theme/custom.customizer-callouts').Aperture | null} [piece]
   * @returns {boolean} False when the copy is missing or no type size fits the frame.
   */
  #paintLaserCallouts(svg, aperture, piece = null) {
    const styles = getComputedStyle(svg);
    const measure = (/** @type {string} */ line, /** @type {number} */ size) => {
      const context = (this.#textCanvas ??= document.createElement('canvas')).getContext('2d');
      if (!context) return line.length * size * 0.5;
      // Measured at 100px and scaled, so the result is in SVG units (percent of the frame). Weight
      // too: the store picks the font, and a bold one would measure narrow and overrun the frame.
      context.font = `${styles.fontWeight} 100px ${styles.fontFamily}`;
      return (context.measureText(line).width / 100) * size;
    };
    const labels = Object.fromEntries(
      Object.entries(svg.dataset)
        .filter(([key]) => key.startsWith('label'))
        .map(([key, value]) => [key.slice(5).toLowerCase(), value ?? ''])
    );
    const plan = planLaserCallouts(aperture, labels, measure, piece);
    if (!plan) return false;
    const leading = laserCalloutLeading(plan.size);
    const ns = 'http://www.w3.org/2000/svg';
    const next = document.createDocumentFragment();
    for (const callout of plan.callouts) {
      const line = document.createElementNS(ns, 'line');
      line.setAttribute('x1', `${callout.x}`);
      line.setAttribute('y1', `${callout.y}`);
      line.setAttribute('x2', `${callout.lineX}`);
      line.setAttribute('y2', `${callout.lineY}`);
      line.setAttribute('stroke', 'currentColor');
      line.setAttribute('stroke-width', `${LASER_CALLOUT_INK.stroke}`);
      next.append(line);
      const dot = document.createElementNS(ns, 'circle');
      dot.setAttribute('cx', `${callout.x}`);
      dot.setAttribute('cy', `${callout.y}`);
      dot.setAttribute('r', `${LASER_CALLOUT_INK.dot}`);
      dot.setAttribute('fill', 'currentColor');
      next.append(dot);
      const text = document.createElementNS(ns, 'text');
      text.setAttribute('x', `${callout.labelX}`);
      text.setAttribute('y', `${callout.labelY}`);
      text.setAttribute('font-size', `${plan.size}`);
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('dominant-baseline', 'middle');
      text.setAttribute('fill', 'currentColor');
      callout.lines.forEach((content, index) => {
        const span = document.createElementNS(ns, 'tspan');
        span.setAttribute('x', `${callout.labelX}`);
        if (index) span.setAttribute('dy', `${leading}`);
        span.textContent = content;
        text.append(span);
      });
      next.append(text);
    }
    svg.replaceChildren(next);
    return true;
  }

  /**
   * The art and window the CURRENT step's photo lands on, asked through the adapter so the drawing
   * frames the same aperture the hero composited - on the view-less products too.
   * @param {CustomizerStore} store
   * @returns {{ base: string, mask: string } | null}
   */
  #photoSurface(store) {
    const step = this.#laserSellingStep(store);
    if (!step || !this.#adapter) return null;
    return this.#adapter.photoAperture(store, step.side ?? null);
  }

  /**
   * An image decoded and ready to scan, once per URL. `crossOrigin` because the scan reads pixels
   * back: art-set masks live on the Shopify CDN, which is CORS-clean, and a tainted read is null.
   * @param {string} url
   * @returns {Promise<HTMLImageElement | null>}
   */
  #loadScan(url) {
    const cached = this.#scanImages.get(url);
    if (cached) return cached;
    const pending = new Promise(
      (/** @type {(value: HTMLImageElement | null) => void} */ resolve) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = () => {
          // A CDN blip must not hide the callouts for the session, nor refetch on every update.
          setTimeout(() => this.#scanImages.delete(url), SCAN_RETRY_MS);
          resolve(null);
        };
        image.src = url;
      }
    );
    this.#scanImages.set(url, pending);
    return pending;
  }

  /**
   * Whether the face on screen draws its shape family's TEMPLATE art, asked through the adapter so
   * it cannot disagree with what the hero composited. Every other art source - a
   * per-product override, the product's own art, an art set, a flat composite - is art the promo
   * graphic was never measured against.
   * @param {CustomizerStore} store
   * @param {CustomizerStep | null} step - The step whose face is sold ({@link #laserSellingStep}).
   * @returns {boolean}
   */
  #faceDrawsTemplateArt(store, step) {
    const doc = /** @type {any} */ (store.configSchema);
    if (!doc || !this.#adapter) return false;
    const view = (doc.views ?? []).find(
      (/** @type {any} */ candidate) => candidate.id === step?.side
    );
    if (!view) return false;
    return this.#adapter.faceArtSource(view, store, doc) === 'template';
  }

  /**
   * WHETHER the selling points show: a schema signal only, read through the same vocabulary the
   * render finish is read through ({@link photoMethodControl} + {@link photoFinish}), so the claim
   * and the finish cannot disagree. The step must take a photo, its Photo mode must be the one
   * selected, and the inserting method in force must etch - the selection, else the schema default,
   * else the lone option, since a one-method face is already decided.
   *
   * KEY-DECISION 2026-09-04: a photo step that offers NO inserting method counts as laser, which is
   * what `photoFinish` answers for a blank method. Those products sell one engraving and never ask,
   * so there is no selection to read and the points belong on the step that edits the photo.
   * @param {CustomizerStore} store
   * @param {CustomizerStep | null} step - The step whose face is sold ({@link #laserSellingStep}).
   * @returns {boolean}
   */
  #laserPromoActive(store, step) {
    if (!step) return false;
    // A step that draws nothing on the piece (a production-only merge photo) sells no engraving.
    // Asked of the current step only: on Review the question is about the face on screen.
    if (!store.isReviewStep && store.stepOutsideComposite) return false;
    return faceAreaSteps(step).some((area) => this.#areaSellsLaser(store, step.id, area));
  }

  /**
   * {@link #laserPromoActive} for one mask on the face.
   * @param {CustomizerStore} store
   * @param {string} stepId
   * @param {CustomizerStep} step - The mask's view of its step.
   * @returns {boolean}
   */
  #areaSellsLaser(store, stepId, step) {
    if (
      (step.controls ?? []).some(
        (control) => isScanUpload(control) && store.isControlActive(control.key)
      )
    )
      return false;
    // The points sell a photo engraving, so a step that takes no photo never shows them.
    if (!(step.controls ?? []).some((control) => control.type === 'upload')) return false;
    // The Photo value is resolved off the face, not compared to 'image': a face may name its only
    // Photo option `image_w_o_edit`.
    const mode = (step.controls ?? []).find(isFaceModeControl);
    if (mode && store.getControlValue(mode.key) !== photoModeValue(mode)) return false;
    const method = photoMethodControl(step);
    // No inserting method on the face at all: laser is the only finish it has, so it is decided.
    if (!method) return true;
    const selected = store.getControlValue(method.key);
    if (typeof selected === 'string' && selected !== '') {
      // Paper is the one finish that never etches, so it is the one that keeps the points off.
      return photoFinish(selected) !== 'paper';
    }
    // KEY-DECISION 2026-09-15: a face the page never ASKS is decided even with no stored value. The
    // seed is withheld only for billing, so waiting on it would hide the claims where no step asks.
    const plan = store.methodDecision(stepId, mode?.key);
    if (plan && !plan.asked && plan.lone) return photoFinish(plan.lone) !== 'paper';
    // A face that still ASKS for a style has not decided on engraving, so the claims wait for the
    // answer rather than riding a schema default the shopper never saw (KEY-DECISION 2026-09-04).
    return false;
  }

  /**
   * The Size step swaps the zoom toggle for the ruler toggle: the step LANDS on the selected size's
   * own image (picking a size swaps the preview), and the ruler
   * toggles to the at-scale family shot and back. "The Size step" = the current step carries the
   * size variant axis, a key-convention mirror of `is_measurement` in custom.customizer-step.liquid
   * (`pendant_size`). Products with no size image at all render no ruler (no `sizeToggle` ref),
   * and every other step gets the zoom toggle back; leaving the step returns to the render.
   * @param {CustomizerStore} store
   */
  #syncSizeMode(store) {
    // Keyed on the size <img>, not the ruler button: the button renders only when the at-scale shot
    // exists, but a product with per-size images and no ruler still needs the landing view.
    const { sizeComparison, sizeToggle } = this.refs;
    if (!sizeComparison) return;
    const step = store.currentStepConfig;
    const onSize = (step?.controls ?? []).some(
      (control) => control.type === 'variant' && control.key === 'pendant_size'
    );
    sizeToggle?.toggleAttribute('hidden', !onSize);
    this.refs.toggle.toggleAttribute('hidden', onSize);
    if (!onSize) {
      this.#setComparing(false);
      this.#setAtScale(false);
    } else {
      // Only reset when ENTERING the step, so the ruler's at-scale view survives an unrelated
      // store update (e.g. picking a different size while comparing at scale).
      if (!this.#wasOnSize) {
        this.#setAtScale(false);
        this.#setComparing(true);
      }
      if (this.hasAttribute('expanded')) {
        // The ruler replaces the zoom on this step — collapse a zoom left open elsewhere so
        // [expanded] can't strand with no button to undo it.
        this.toggleAttribute('expanded', false);
        this.#syncToggle(false);
      }
    }
    this.#wasOnSize = onSize;
  }

  /**
   * Point the Size-step <img> at the right image:
   * - per-size view (landing): the SELECTED size's own chart (`data-variant-charts`, keyed by
   *   Shopify variant id), then product-level `data-default-src`, then the at-scale shot, so the
   *   img is never empty on a product whose variants carry no `magento.size_chart`.
   * - at-scale view ([at-scale], ruler pressed): the family shot (`data-ruler-src`, the
   *   customizer_shape metaobject image).
   * Runs on every store update, but only mutates `src` when it actually changes, so an unrelated
   * update (engraving keystroke, metal swap) costs nothing and never restarts the cross-fade.
   * @param {CustomizerStore} store
   */
  #syncSizeComparison(store) {
    const { sizeComparison } = this.refs;
    if (!sizeComparison) return;
    const map = this.#variantCharts();
    const id = store.selectedVariant?.id;
    const rulerSrc = sizeComparison.dataset.rulerSrc || '';
    const chartSrc =
      (id !== undefined && map[String(id)]) || sizeComparison.dataset.defaultSrc || rulerSrc;
    const src = this.#atScale ? rulerSrc || chartSrc : chartSrc;
    if (src && sizeComparison.getAttribute('src') !== src) {
      sizeComparison.setAttribute('src', src);
    }
    // Alt tracks the bitmap actually shown, not just #atScale: the at-scale shot can also stand in
    // as the landing image when a product has no per-size chart (chartSrc falls back to rulerSrc).
    const atScaleShown = !!rulerSrc && src === rulerSrc;
    const alt = atScaleShown ? sizeComparison.dataset.altRuler : sizeComparison.dataset.altChart;
    if (alt && sizeComparison.getAttribute('alt') !== alt) {
      sizeComparison.setAttribute('alt', alt);
    }
  }

  /**
   * Two steps put CATALOG PHOTOGRAPHY on the stage instead of a composite: the Size step's per-size
   * chart / at-scale shot, and the chain step's browse image. Neither is shot for the square stage,
   * so the frame takes the art's own ratio (`--customizer-art-ratio`) and the contain fit then
   * fills it whole. Everything else, the photo editor included, keeps the square the editor centres
   * product art in.
   * @param {CustomizerStore} store
   */
  #syncStageRatio(store) {
    const art = this.#stageArt(store);
    if (!art) {
      this.#setArtRatio('');
      return;
    }
    const { naturalWidth: width, naturalHeight: height } = art;
    // Natural size reads 0 until a just-pointed src decodes, so hold the ratio already on screen
    // and let the load listener take it; no resolved src at all means there is no art to fit.
    if (width && height) this.#setArtRatio(String(width / height));
    else if (!art.currentSrc) this.#setArtRatio('');
  }

  /**
   * The <img> owning the stage on such a step, null wherever the composite owns it.
   * @param {CustomizerStore} store
   * @returns {HTMLImageElement | null}
   */
  #stageArt(store) {
    if (this.hasAttribute('editing')) return null;
    if (this.hasAttribute('comparing')) return this.refs.sizeComparison ?? null;
    const step = store.currentStepConfig;
    // Key-convention mirror of the store's chain step, gated on it actually holding a chain shot:
    // with none the stage stays the locket composite, which belongs in the square.
    if (step?.unresolvedRef?.type !== 'chain_group' || !store.heroImageOverride) return null;
    const image = this.querySelector('.custom-customizer-preview__image');
    return image instanceof HTMLImageElement ? image : null;
  }

  /** @param {string} ratio - Width/height as a number, or '' to restore the square stage. */
  #setArtRatio(ratio) {
    if (ratio === this.#artRatio) return;
    this.#artRatio = ratio;
    if (ratio) this.style.setProperty('--customizer-art-ratio', ratio);
    else this.style.removeProperty('--customizer-art-ratio');
    this.toggleAttribute('art-ratio', ratio !== '');
  }

  /** Re-measure once a stage bitmap lands: until then its natural size is unknown. */
  #handleArtLoad = () => {
    const host = /** @type {{ store?: CustomizerStore } | null} */ (
      this.closest('custom-customizer')
    );
    if (host?.store) this.#syncStageRatio(host.store);
  };

  /**
   * The Review corner: whichever frame {@link planFrame} put there - the render as the proof of
   * what was personalized, or the product photo once the shopper trades the two. `null` hides it.
   *
   * Not keyed on the step: the plan already decided this frame belongs in the corner, and keeping
   * that decision in one place stops the two from drifting apart.
   * @param {string | null} url
   */
  #syncReviewInset(url) {
    // Ahead of the ref guard: the corner leaving the screen forgets the trade whether or not this
    // store renders the button, so Review always opens product-first rather than mid-swap.
    if (!url) this.#insetSwapped = false;
    const { reviewInset: inset, reviewInsetButton: button } = this.refs;
    if (!inset || !button) return;
    // A revoked blob would paint a broken thumbnail. The button still follows `url`, so the next
    // render fills the inset.
    const live = !url || !url.startsWith('blob:') || this.#engine.isCached(url);
    if (url && live && inset.src !== url) inset.src = url;
    button.toggleAttribute('hidden', !url);
  }

  /** Trade the Review inset with the stage: tap the thumbnail and personalization takes over,
   * tap again to go back. The flag is all this holds, #render owns which URL lands where. */
  swapReviewInset() {
    this.#insetSwapped = !this.#insetSwapped;
    const host = /** @type {{ store?: CustomizerStore } | null} */ (
      this.closest('custom-customizer')
    );
    if (host?.store) this.#scheduleRender(host.store);
  }

  /** The bubble is the scan itself in a small disc, the shopper's proof beside the piece. Which
   * step it shows on is a store setting, read off the snippet's dataset.
   * @param {CustomizerStore} store
   */
  #syncFingerprintBubble(store) {
    const bubble = this.refs.fingerprintBubble;
    if (!bubble) return;
    const show = store.isReviewStep
      ? this.dataset.fingerprintBubbleReview !== 'false'
      : this.dataset.fingerprintBubbleCustomize !== 'false';
    const src = show ? fingerprintScan(store) : '';
    bubble.toggleAttribute('hidden', !src);
    if (src && bubble.getAttribute('src') !== src) bubble.setAttribute('src', src);
    const alt =
      fingerprintScanKind(store) === 'handwriting'
        ? bubble.dataset.altHandwriting
        : bubble.dataset.altFingerprint;
    if (src && alt && bubble.alt !== alt) bubble.alt = alt;
  }

  /**
   * Parse (once) the per-size chart map off the size-comparison <img>. A missing or malformed
   * attribute yields an empty map, so the fallback in {@link #syncSizeComparison} takes over.
   * @returns {Record<string, string>}
   */
  #variantCharts() {
    if (this.#sizeChartMap) return this.#sizeChartMap;
    const raw = this.refs.sizeComparison?.dataset.variantCharts;
    /** @type {Record<string, string>} */
    let map = {};
    try {
      if (raw) map = JSON.parse(raw);
    } catch {
      map = {};
    }
    this.#sizeChartMap = map;
    return map;
  }

  /** Ruler toggle: swap the Size-step art between the at-scale shot and the per-size image. */
  toggleComparison() {
    this.#setAtScale(!this.#atScale);
    const host = /** @type {{ store?: CustomizerStore } | null} */ (
      this.closest('custom-customizer')
    );
    if (host?.store) this.#syncSizeComparison(host.store);
  }

  /** @param {boolean} on */
  #setComparing(on) {
    // The <img> stays in the tree at opacity 0 off-step, so it is NOT toggled hidden here: that
    // would kill the cross-fade. [comparing] holds the art over the render for the whole step.
    this.toggleAttribute('comparing', on);
  }

  /** @param {boolean} on - The [at-scale] attribute drives the ruler⇄profile icon swap in CSS. */
  #setAtScale(on) {
    this.#atScale = on;
    this.toggleAttribute('at-scale', on);
    this.refs.sizeToggle?.setAttribute('aria-pressed', String(on));
  }

  /** The loading window spans spec build, engine render and the new frame's decode. Delay the
   * blur and spinner, so a change resolving in a beat (warm cache, a keystroke) never flickers. */
  #beginLoading() {
    this.#pending++;
    if (this.#pending > 1) return;
    this.#loadingTimer = window.setTimeout(() => {
      if (this.#pending > 0) this.toggleAttribute('loading', true);
    }, 150);
  }

  #endLoading() {
    this.#pending = Math.max(0, this.#pending - 1);
    if (this.#pending > 0) return;
    clearTimeout(this.#loadingTimer);
    this.toggleAttribute('loading', false);
  }

  /** @returns {import('@theme/custom.customizer-adapter').CustomizerAssets} */
  #readAssets() {
    return /** @type {import('@theme/custom.customizer-adapter').CustomizerAssets} */ (
      readIsland(this.closest('custom-customizer'), 'assets') ?? {}
    );
  }

  /** The stacked (mobile) customizer layout — below the 1024px side-by-side breakpoint. */
  static #MOBILE_QUERY = '(max-width: 1023px)';

  /** One [expanded] state, two CSS behaviours: mobile grows the image container downward in
   * place, desktop scales the composite inside the same frame. Never a fullscreen takeover. */
  toggle = () => {
    const expanded = !this.hasAttribute('expanded');
    this.toggleAttribute('expanded', expanded);
    this.#syncToggle(expanded);
    // Desktop zoom rescales the editor's stage without changing its box, so its resize rebuild
    // never fires: ask for it directly. On mobile the box grows and the observer handles it.
    if (!window.matchMedia(CustomizerPreview.#MOBILE_QUERY).matches) {
      const editor = /** @type {{ refresh?: () => void } | null} */ (
        this.querySelector('custom-customizer-photo-editor')
      );
      editor?.refresh?.();
    }
  };

  /** Open the composite over the whole viewport: the shared entry point for both engraving
   * Preview actions in different components. No-op with nothing rendered or a revoked src. */
  openFullscreen() {
    const { fullscreen, fullscreenImage } = this.refs;
    const composite = /** @type {HTMLImageElement | null} */ (
      this.querySelector('.custom-customizer-preview__image')
    );
    const src = composite?.currentSrc || composite?.src;
    if (!fullscreen || !fullscreenImage || !src) return;
    // The src may be a blob composite evicted AND revoked since it painted; copying a revoked blob
    // into the overlay shows a broken image, so skip. Same guard as the crossfade ghost.
    if (src.startsWith('blob:') && !this.#engine.isCached(src)) return;

    fullscreenImage.src = src;
    fullscreenImage.alt = composite?.alt ?? '';
    fullscreenImage.removeAttribute('hidden');
    fullscreen.removeAttribute('hidden');
    document.addEventListener('keydown', this.#onFullscreenKeydown, true);
    this.#releaseFullscreenCancel();
    this.#releaseFullscreenCancel = holdDialogCancel(this, () => this.closeFullscreen());
    this.#fullscreenReturnFocus = /** @type {HTMLElement | null} */ (
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    );
    this.refs.fullscreenClose?.focus({ preventScroll: true });
  }

  /** Close the fullscreen viewer (X, backdrop/image tap, Escape, or overlay reopen). Idempotent. */
  closeFullscreen() {
    const { fullscreen } = this.refs;
    if (!fullscreen || fullscreen.hasAttribute('hidden')) return;
    fullscreen.setAttribute('hidden', '');
    document.removeEventListener('keydown', this.#onFullscreenKeydown, true);
    this.#releaseFullscreenCancel();
    this.#releaseFullscreenCancel = () => {};
    // Preview hides its own button as it opens the viewer, so the step title takes focus instead.
    const target =
      this.#fullscreenReturnFocus?.checkVisibility?.() === false
        ? this.closest('custom-customizer')?.querySelector('.custom-customizer-step__title')
        : this.#fullscreenReturnFocus;
    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
    this.#fullscreenReturnFocus = null;
  }

  /**
   * Escape closes only the fullscreen viewer - caught in the capture phase and stopped so the
   * open <dialog> doesn't also treat it as a request to close the whole customizer. Tab stays on
   * the viewer's one control, so it never reaches the wizard hidden behind it.
   * @param {KeyboardEvent} event
   */
  #onFullscreenKeydown = (event) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      this.refs.fullscreenClose?.focus({ preventScroll: true });
      return;
    }
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.closeFullscreen();
  };

  /**
   * @param {boolean} open
   */
  #syncToggle(open) {
    const { toggle } = this.refs;
    toggle.setAttribute('aria-expanded', String(open));
    const label = open ? toggle.dataset.labelCollapse : toggle.dataset.labelExpand;
    if (label) toggle.setAttribute('aria-label', label);
  }

  /**
   * Fresh session, fresh zoom: reopening the overlay collapses a zoom left expanded last time and
   * closes a fullscreen viewer left open.
   * @param {Event} event
   */
  #handleOverlayOpen = (event) => {
    const { configId } = /** @type {CustomEvent} */ (event).detail ?? {};
    const host = /** @type {HTMLElement | null} */ (this.closest('custom-customizer'));
    if (!host || configId !== host.dataset.configId) return;
    this.toggleAttribute('expanded', false);
    this.#syncToggle(false);
    this.closeFullscreen();
    // A close leaves #wasOnSize true, so #syncSizeMode would skip its enter-step reset and a reopen
    // on the Size step would never restore the per-size image. Clear all three, then re-sync.
    this.#setComparing(false);
    this.#setAtScale(false);
    this.#wasOnSize = false;
    const store = /** @type {{ store?: CustomizerStore }} */ (host).store;
    if (store) {
      this.#syncSizeMode(store);
      this.#syncSizeComparison(store);
      this.#syncStageRatio(store);
    }
    // A ghost left mid-fade by the closing session must not sit over the fresh one's composite.
    this.refs.ghost?.classList.remove('custom-customizer-preview__ghost--showing');
  };
}

if (!customElements.get('custom-customizer-preview')) {
  customElements.define('custom-customizer-preview', CustomizerPreview);
}
